import { useSyncExternalStore } from 'react';

/**
 * Dev-only switch that pins every `QueryBoundary` to its loading branch.
 *
 * @remarks
 *
 * Skeletons are meant to hold the exact space the real content takes, which is
 * only checkable by flipping between the two on the same page at the same
 * scroll position. Waiting on a slow network gives one look at the skeleton and
 * no way back; this holds it open instead. Queries still run, so switching off
 * swaps in cached data immediately.
 *
 * Off, and inert, outside `npm run dev` — the state never leaves `false` in a
 * production build.
 *
 * Turn it on with `?skeleton` in the URL, the badge in the bottom-left corner,
 * or Alt+Shift+S. The choice survives a reload (sessionStorage) so an
 * edit/refresh loop stays in whichever mode you were working in.
 */
const STORAGE_KEY = 'mdc:skeleton-mode';

const listeners = new Set<() => void>();
let enabled = false;

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return enabled;
}

/** Always `false` outside the browser (and in production). */
function getServerSnapshot() {
  return false;
}

export function isSkeletonMode() {
  return enabled;
}

export function setSkeletonMode(next: boolean) {
  if (!import.meta.env.DEV || next === enabled) {
    return;
  }

  enabled = next;

  try {
    sessionStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // Private-mode storage failures don't matter to a dev toggle.
  }

  emit();
}

export function toggleSkeletonMode() {
  setSkeletonMode(!enabled);
}

/** Re-renders the caller whenever the toggle flips. */
export function useSkeletonMode() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

// Module scope, so StrictMode's double-invoked effect re-registers the same
// reference — which the DOM ignores — instead of stacking a second handler that
// would toggle the state straight back.
function onKeyDown(event: KeyboardEvent) {
  // Alt+Shift+S — unclaimed by the browsers this is developed in.
  if (event.altKey && event.shiftKey && event.code === 'KeyS') {
    event.preventDefault();
    toggleSkeletonMode();
  }
}

/**
 * Reads the initial state and registers the keyboard shortcut. Called from the
 * app root; a no-op in production.
 */
export function initSkeletonMode() {
  if (!import.meta.env.DEV || typeof window === 'undefined') {
    return;
  }

  const fromUrl = new URLSearchParams(window.location.search).has('skeleton');
  let stored = false;

  try {
    stored = sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // Ignore — see setSkeletonMode.
  }

  const next = fromUrl || stored;

  if (next !== enabled) {
    enabled = next;
    emit();
  }

  window.addEventListener('keydown', onKeyDown);

  return () => {
    window.removeEventListener('keydown', onKeyDown);
  };
}
